import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { apiFetchInit, apiOrigin } from "@/lib/apiOrigin";
import { atwAuthSignoutUrl } from "@/lib/atwAuthHost";
import { atwAgentsAppUrl } from "@/lib/atwAgentsHost";

export type AgentsSessionUser = {
  id?: string;
  email?: string | null;
  name?: string | null;
  role?: string | null;
  orgId?: string | null;
};

type SessionState = {
  data: { user?: AgentsSessionUser } | null;
  status: "loading" | "authenticated" | "unauthenticated";
  refresh: () => void;
};

const SessionContext = createContext<SessionState>({
  data: null,
  status: "loading",
  refresh: () => {},
});

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<SessionState, "refresh">>({
    data: null,
    status: "loading",
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setState((s) => ({ ...s, status: "loading" }));
      try {
        const res = await fetch(`${apiOrigin()}/api/auth/session`, {
          credentials: "include",
          cache: "no-store",
          ...apiFetchInit(),
        });
        if (cancelled) return;
        if (!res.ok) {
          setState({ data: null, status: "unauthenticated" });
          return;
        }
        const data = (await res.json()) as { user?: AgentsSessionUser };
        if (data?.user?.email) {
          setState({ data: { user: data.user }, status: "authenticated" });
        } else {
          setState({ data: null, status: "unauthenticated" });
        }
      } catch {
        if (!cancelled) setState({ data: null, status: "unauthenticated" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refresh = () => setTick((t) => t + 1);

  return (
    <SessionContext.Provider value={{ ...state, refresh }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  return useContext(SessionContext);
}

export function signOut() {
  window.location.assign(atwAuthSignoutUrl(atwAgentsAppUrl("/")));
}
